import type { BoltOrder, BoltOrderStatus } from "./boltutil-types";
import { safeBoltCheckoutUrl } from "./boltutil-types";

/** UI labels never imply that receipt confirmation credits the CNY wallet. */
export function boltStatus(status: BoltOrderStatus, reviewed = false) {
  if (status === "confirmed") return reviewed ? "已收款，已人工核对（不自动入账）" : "已收款，待人工核账";
  return ({ creating: "正在确认建单结果", pending: "等待付款或收款同步", unknown: "结果待核对，请勿重复付款", expired: "订单已过期，请勿付款" })[status];
}

/** Credit is shown only when the server includes a committed CNY ledger record. */
export function formatBoltCny(cents: string | number) {
  if ((typeof cents === "number" && !Number.isSafeInteger(cents)) || !/^\d+$/.test(String(cents))) return "待确认";
  const value = BigInt(cents);
  return `${value / 100n}.${String(value % 100n).padStart(2, "0")}`;
}
export function boltOrderStatus(order: Pick<BoltOrder, "status" | "quote" | "credit" | "settlementIssue">, reviewed = false) {
  if (order.credit) return `${order.credit.policyVersion === "bolt-legacy-approved-v1" ? "历史补账已入账" : "已入账"} ¥${formatBoltCny(order.credit.amountCents)}`;
  if (order.settlementIssue) return "结算异常，待处理";
  if (order.quote && order.status === "confirmed") return "已收款，正在自动入账";
  return boltStatus(order.status, reviewed);
}

/** Missing local evidence is not evidence that the payer has not paid. */
export function boltReceiptDisplay(order: Pick<BoltOrder, "receipt" | "deferredReceipt">) {
  return {
    paidAmount: order.receipt?.paidAmount ?? (order.deferredReceipt ? "待核对（见回执）" : "待核对"),
    txHash: order.receipt?.txHash ?? (order.deferredReceipt ? "见待核对回执" : "尚未获取收款凭据"),
    note: order.receipt ? undefined : order.deferredReceipt
      ? "已获取回执，原始应付资料仍待核对；请勿重复付款。"
      : "尚未获取收款凭据，不能据此判断未付款。若已付款，请核对收款状态，勿重复付款。",
  };
}

export function boltLocalRefreshHint(queryAction = "核对收款状态") {
  return `刷新仅重读本站记录；${queryAction}才查询供应商。`;
}

/** A successful HTTP response can still contain an unresolved provider result. */
export function boltRefreshFeedback(order: Pick<BoltOrder, "status" | "receipt" | "review" | "quote" | "credit" | "settlementIssue">) {
  if (order.credit) return `${order.credit.policyVersion === "bolt-legacy-approved-v1" ? "历史补账已入账" : "已确认入账"} ¥${formatBoltCny(order.credit.amountCents)}，请查看平台余额及资金记录；不会重复加款。`;
  if (order.settlementIssue) return `收款已查询，结算仍待处理：${order.settlementIssue}`;
  if (order.quote && order.status === "confirmed" && order.receipt) return "已确认收款，系统正在核验并自动入账，请稍后刷新记录；无需人工加款。";
  if (order.status === "confirmed" && order.receipt) return order.review
    ? "已确认收款，已记录人工核对；本次查询未修改会员余额。"
    : "已确认收款，待人工核账；未修改会员余额。";
  if (order.status === "pending") return "等待付款或收款同步；若已付款请勿重复付款，未修改会员余额。";
  if (order.status === "expired") return "上游返回订单已过期；如已付款请联系财务核对，未修改会员余额。";
  return "查询已返回，收款结果仍待核对；未修改会员余额。";
}
export function safeBoltCheckout(value?: string) {
  return safeBoltCheckoutUrl(value);
}
export function boltTime(value?: string | number) {
  if (value === undefined) return "未提供";
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? new Intl.DateTimeFormat("zh-CN", { timeZone: "Asia/Shanghai", dateStyle: "short", timeStyle: "short" }).format(date) : "未提供";
}
